import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { Hanko } from "@teamhanko/hanko-elements";

const hankoApi = process.env.BUN_PUBLIC_HANKO_API_URL as string;

interface User {
  id: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  hanko: Hanko | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [hanko, setHanko] = useState<Hanko | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const hankoInstance = new Hanko(hankoApi);
    setHanko(hankoInstance);
  }, []);

  useEffect(() => {
    if (!hanko) return;

    const checkSession = async () => {
      try {
        const session = await hanko.validateSession();
        if (session.is_valid) {
          const currentUser = await hanko.getUser();
          const primaryEmail = currentUser.emails?.find((e) => e.is_primary);
          setUser({
            id: currentUser.user_id,
            email: primaryEmail?.address,
          });
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Session check failed:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    const unsubscribeSessionCreated = hanko.onSessionCreated(() => {
      checkSession();
    });

    const unsubscribeSessionExpired = hanko.onSessionExpired(() => {
      setUser(null);
    });

    const unsubscribeUserDeleted = hanko.onUserDeleted(() => {
      setUser(null);
    });

    const unsubscribeUserLoggedOut = hanko.onUserLoggedOut(() => {
      setUser(null);
    });

    return () => {
      unsubscribeSessionCreated();
      unsubscribeSessionExpired();
      unsubscribeUserDeleted();
      unsubscribeUserLoggedOut();
    };
  }, [hanko]);

  const logout = useCallback(async () => {
    if (!hanko) return;
    try {
      await hanko.logout();
      setUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, [hanko]);

  const isAuthenticated = user !== null;

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      logout,
      hanko,
    }),
    [user, isAuthenticated, isLoading, logout, hanko]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
